import { useState, useRef, useEffect } from 'react'
import { enqueueSnackbar } from 'notistack'
import Typography from '../../atoms/Typography'

export type ParseResult<T> = { value: T; valid: true } | { valid: false; error: string }

interface EditableCellProps<T> {
  value: T
  minWidth?: string
  inputClassName?: string
  inputType?: string
  displayValue?: (v: T) => React.ReactNode
  parseValue: (raw: string) => ParseResult<T>
  onSave: (value: T) => Promise<void>
}

const EditableCell = <T,>({
  value: externalValue,
  minWidth = 'min-w-[100px]',
  inputClassName = '',
  inputType = 'text',
  displayValue,
  parseValue,
  onSave,
}: EditableCellProps<T>) => {
  const [isEditing, setIsEditing] = useState(false)
  const [raw, setRaw] = useState(String(externalValue ?? ''))
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      if (inputType === 'text') inputRef.current.select()
    }
  }, [isEditing, inputType])

  useEffect(() => {
    if (!isEditing) {
      setRaw(String(externalValue ?? ''))
    }
  }, [externalValue, isEditing])

  const save = async (input: string) => {
    setIsEditing(false)
    const result = parseValue(input)
    if (!result.valid) {
      enqueueSnackbar(result.error, { variant: 'error' })
      setRaw(String(externalValue ?? ''))
      return
    }
    if (result.value === externalValue) return
    try {
      await onSave(result.value)
    } catch {
      setRaw(String(externalValue ?? ''))
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      inputRef.current?.blur()
    } else if (e.key === 'Escape') {
      setRaw(String(externalValue ?? ''))
      setIsEditing(false)
    }
  }

  const display = displayValue ? displayValue(externalValue) : String(externalValue ?? '')

  return (
    <div className={minWidth}>
      {isEditing ? (
        <input
          ref={inputRef}
          type={inputType}
          className={`rounded border border-gray-600 bg-gray-800 px-2 py-1 text-white outline-none focus:border-light-green-100 ${inputClassName}`}
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          onBlur={(e) => save(e.target.value)}
          onKeyDown={handleKeyDown}
        />
      ) : (
        <Typography
          variant="base"
          className="cursor-pointer text-white transition-colors hover:text-blue-400"
          onClick={() => setIsEditing(true)}
        >
          {display}
        </Typography>
      )}
    </div>
  )
}

export default EditableCell
